import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export interface EquipmentOption {
  id?: number;
  value: string;
  label: string;
  description?: string;
  specifications?: any;
  isCustom?: boolean;
}

export interface EquipmentOptionsData {
  panels: EquipmentOption[];
  inverters: {
    sigenergySinglePhase: EquipmentOption[];
    sigenergyThreePhase: EquipmentOption[];
    hanchuSinglePhase: EquipmentOption[];
    custom: EquipmentOption[];
  };
  batteries: {
    sigen: EquipmentOption[];
    hanchu: EquipmentOption[];
    custom: EquipmentOption[];
  };
}

// Fetch all equipment options (hardcoded + custom)
export function useEquipmentOptions() {
  return useQuery<EquipmentOptionsData>({
    queryKey: ['equipment-options'],
    queryFn: async () => {
      const response = await fetch('/api/equipment-options', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch equipment options');
      }

      const result = await response.json();
      return result.data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
}

// Create a new equipment option
export function useCreateEquipmentOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      type: 'panel' | 'inverter' | 'battery';
      category?: string;
      value: string;
      label: string;
      description?: string;
      specifications?: any;
    }) => {
      const response = await fetch('/api/equipment-options', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create equipment option');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate the equipment options cache to refetch the updated list
      queryClient.invalidateQueries({ queryKey: ['equipment-options'] });
    },
  });
}

// Delete an equipment option
export function useDeleteEquipmentOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (optionId: number) => {
      const response = await fetch(`/api/equipment-options/${optionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete equipment option');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate the equipment options cache
      queryClient.invalidateQueries({ queryKey: ['equipment-options'] });
    },
  });
}

// Update an equipment option
export function useUpdateEquipmentOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: {
      id: number;
      label?: string;
      description?: string;
      specifications?: any;
      category?: string;
    }) => {
      const response = await fetch(`/api/equipment-options/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update equipment option');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate the equipment options cache
      queryClient.invalidateQueries({ queryKey: ['equipment-options'] });
    },
  });
}

// Helper function to format equipment options for select components
export function formatEquipmentOptionsForSelect(
  options: EquipmentOptionsData,
  type: 'panel' | 'inverter' | 'battery'
) {
  if (type === 'panel') {
    return options.panels;
  }

  if (type === 'inverter') {
    const groups = [];

    if (options.inverters.sigenergySinglePhase.length > 0) {
      groups.push({
        label: 'Sigenergy - Single Phase',
        options: options.inverters.sigenergySinglePhase,
      });
    }

    if (options.inverters.sigenergyThreePhase.length > 0) {
      groups.push({
        label: 'Sigenergy - Three Phase',
        options: options.inverters.sigenergyThreePhase,
      });
    }

    if (options.inverters.hanchuSinglePhase.length > 0) {
      groups.push({
        label: 'Hanchu - Single Phase',
        options: options.inverters.hanchuSinglePhase,
      });
    }

    if (options.inverters.custom.length > 0) {
      groups.push({
        label: 'Other Inverters',
        options: options.inverters.custom,
      });
    }

    return groups;
  }

  if (type === 'battery') {
    const groups = [];

    if (options.batteries.sigen.length > 0) {
      groups.push({
        label: 'Sigen Batteries',
        options: options.batteries.sigen,
      });
    }

    if (options.batteries.hanchu.length > 0) {
      groups.push({
        label: 'Hanchu Batteries',
        options: options.batteries.hanchu,
      });
    }

    if (options.batteries.custom.length > 0) {
      groups.push({
        label: 'Other Batteries',
        options: options.batteries.custom,
      });
    }

    return groups;
  }

  return [];
}